import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Printer } from "lucide-react";
import {
  prescriptionQueryKeys,
  prescriptionsApi,
} from "../../shared/api/prescriptions";
import { apiError } from "../../shared/api/client";
import { patientsApi } from "../../shared/api/patients";
import type {
  PrescriptionAllergyConflict,
  PrescriptionStatus,
} from "../../shared/types/prescription";
import type { AllergyOverrideReason, DrugAllergyWarning } from "../../shared/types/patient";
import { formatDate } from "../../shared/format";
import { GovRxPanel } from "./components/GovRxPanel";
import { DispensePanel } from "./components/DispensePanel";
import {
  PrescriptionAllergyConflicts,
  PrescriptionAllergyOverrides,
} from "./components/PrescriptionAllergyConflicts";
import { DrugAllergyWarningDialog } from "../encounters/components/DrugAllergyGuard";
import { PatientLink } from "../../shared/ui/PatientLink";
import { Button } from "@/components/ui/button";
import { Badge, type BadgeProps } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

type BadgeVariant = NonNullable<BadgeProps["variant"]>;

const STATUS_LABEL: Record<PrescriptionStatus, string> = {
  DRAFT: "Nháp",
  SIGNED: "Đã hoàn tất",
  CANCELLED: "Đã hủy",
};
const STATUS_VARIANT: Record<PrescriptionStatus, BadgeVariant> = {
  DRAFT: "warning",
  SIGNED: "success",
  CANCELLED: "destructive",
};

/** The server's refusal to finalize while an allergy warning is unanswered (#18859/#18861). */
const ALLERGY_OVERRIDE_REQUIRED = "prescription_allergy_override_required";

const ALLERGY_DECLINED_MESSAGE =
  "Chưa hoàn tất đơn: đơn có thuốc bệnh nhân đang dị ứng. Đơn vẫn ở trạng thái Nháp — bỏ thuốc đó khỏi đơn, hoặc bấm “Hoàn tất đơn (ký giấy)” lại, chọn lý do rồi bấm “Vẫn kê”.";

type AllergyGateItem = {
  medicineId: string;
  medicineName: string;
  warnings: DrugAllergyWarning[];
};

/**
 * Unanswered conflicts, one dialog per medicine — the shape the encounter screen's gate uses
 * (#19184), so the doctor sees the same question in both places.
 */
export function allergyGateQueue(conflicts: PrescriptionAllergyConflict[]): AllergyGateItem[] {
  const byMed = new Map<string, AllergyGateItem>();
  for (const c of conflicts) {
    if (c.overridden) continue;
    const entry = byMed.get(c.medicineId) ?? {
      medicineId: c.medicineId,
      medicineName: c.medicineName,
      warnings: [],
    };
    entry.warnings.push({
      allergyId: c.allergyId,
      substance: c.substance,
      severity: c.severity,
      reaction: c.reaction,
      matchedOn: c.matchedOn,
      severe: c.severe,
      overridden: false,
    });
    byMed.set(c.medicineId, entry);
  }
  return [...byMed.values()];
}

/**
 * A Vietnamese sentence for a failed action — never axios' "Request failed with status code 422"
 * (#19184). The server's own `detail` is Vietnamese for every business refusal, so it is used when
 * present; transport and server faults get a fixed sentence rather than whatever the error carried.
 */
export function prescriptionActionError(e: unknown, action: string): string {
  const err = apiError(e);
  if (err.code === ALLERGY_OVERRIDE_REQUIRED) {
    return "Chưa hoàn tất đơn: đơn có thuốc bệnh nhân đang dị ứng, cần xác nhận và nêu lý do trước khi hoàn tất.";
  }
  if (err.status === 0) {
    return `Không ${action} được: không kết nối được máy chủ. Vui lòng kiểm tra mạng và thử lại.`;
  }
  if (err.status === 401) return `Không ${action} được: phiên đăng nhập đã hết hạn.`;
  if (err.status === 403) return `Không ${action} được: bạn không có quyền thao tác này.`;
  if (err.status >= 500) {
    return `Không ${action} được: máy chủ đang gặp lỗi, vui lòng thử lại sau ít phút.`;
  }
  const detail = typeof err.problem?.detail === "string" ? err.problem.detail.trim() : "";
  return detail ? `Không ${action} được: ${detail}` : `Không ${action} được. Vui lòng thử lại.`;
}

export function PrescriptionDetailPage() {
  const { id = "" } = useParams();
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const query = useQuery({
    queryKey: prescriptionQueryKeys.detail(id),
    queryFn: () => prescriptionsApi.get(id),
    enabled: !!id,
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: prescriptionQueryKeys.detail(id) });
    qc.invalidateQueries({ queryKey: prescriptionQueryKeys.all });
  };

  // #19184 — the allergy dialog in front of "Hoàn tất đơn": one medicine at a time.
  const [allergyGate, setAllergyGate] = useState<AllergyGateItem[] | null>(null);
  const [answering, setAnswering] = useState(false);

  /** Freshly computed conflicts — the allergy may have been recorded after the page opened. */
  async function unansweredConflicts(): Promise<AllergyGateItem[] | null> {
    try {
      const fresh = await query.refetch();
      if (fresh.data) return allergyGateQueue(fresh.data.allergyConflicts ?? []);
    } catch {
      // fall through
    }
    return null;
  }

  const finalize = useMutation({
    mutationFn: () => prescriptionsApi.finalize(id),
    onSuccess: invalidate,
    onError: async (e: unknown) => {
      // Fallback for when the pre-check missed (it failed, or an allergy was recorded in the
      // seconds between): the server's refusal opens the same dialog instead of a raw error.
      if (apiError(e).code === ALLERGY_OVERRIDE_REQUIRED) {
        const queue = await unansweredConflicts();
        if (queue && queue.length > 0) {
          setAllergyGate(queue);
          return;
        }
      }
      setError(prescriptionActionError(e, "hoàn tất đơn"));
    },
  });

  const cancel = useMutation({
    mutationFn: (reason: string) => prescriptionsApi.cancel(id, reason),
    onSuccess: invalidate,
    onError: (e: unknown) => setError(prescriptionActionError(e, "hủy đơn")),
  });

  /**
   * "Hoàn tất đơn (ký giấy)": ask about every unanswered allergy BEFORE calling finalize, as the
   * encounter screen does (#19184). The server still refuses on its own — this is the courtesy,
   * the guard in finalizePrescription is the safety net.
   */
  async function startFinalize() {
    setError(null);
    const queue = await unansweredConflicts();
    if (queue && queue.length > 0) {
      setAllergyGate(queue);
      return;
    }
    finalize.mutate();
  }

  async function answerAllergyGate(reasonCode: AllergyOverrideReason, reasonText: string | null) {
    const gate = allergyGate;
    const current = query.data;
    if (!gate || gate.length === 0 || !current) return;
    const [head, ...rest] = gate;
    setAnswering(true);
    try {
      // One row per matched allergy — the guard checks each (medicine, allergy) pair. Anchored to
      // this prescription; findCovering also counts it when the visit is signed.
      await Promise.all(
        head.warnings.map((w) =>
          patientsApi.overrideDrugAllergy(current.patientId, {
            medicineId: head.medicineId,
            allergyId: w.allergyId,
            prescriptionId: current.id,
            reasonCode,
            reasonText,
          })
        )
      );
    } catch (e2) {
      setAllergyGate(null);
      setError(prescriptionActionError(e2, "ghi xác nhận dị ứng"));
      return;
    } finally {
      setAnswering(false);
    }
    if (rest.length > 0) {
      setAllergyGate(rest);
      return;
    }
    setAllergyGate(null);
    invalidate();
    finalize.mutate();
  }

  if (query.isLoading)
    return <p className="text-sm text-muted-foreground">Đang tải…</p>;
  if (query.isError || !query.data)
    return (
      <p className="text-sm text-destructive">Không tìm thấy đơn thuốc.</p>
    );

  const rx = query.data;

  return (
    <div>
      {/* #18861 — an allergy recorded after this draft was written shows up here, on open. */}
      <PrescriptionAllergyConflicts conflicts={rx.allergyConflicts ?? []} />
      {/* #19184 — who answered which allergy warning, why, and when. */}
      <PrescriptionAllergyOverrides overrides={rx.allergyOverrides ?? []} />
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="space-y-1">
          <h1 className="flex flex-wrap items-center gap-2 text-xl font-semibold text-foreground">
            Đơn thuốc
            <Badge variant={STATUS_VARIANT[rx.status]}>
              {STATUS_LABEL[rx.status]}
            </Badge>
          </h1>
          {/* #19345 — `doctorName` là tên ĐÃ MANG học hàm/học vị từ hồ sơ nhân sự ("BS. Nguyễn
              Văn An", "ThS.BS. Nguyễn Thị Hồng"), nên thêm "BS. " ở đây ra "BS. BS. Nguyễn Văn
              An". Cột Bác sĩ ở danh sách và đơn in A5 đều vẽ thẳng `doctorName`; màn này là chỗ
              duy nhất tự thêm tiền tố. Không đổi thành "thêm có điều kiện": tiền tố thuộc dữ
              liệu nhân sự (có người là ThS.BS., BS.CKII., DS.), và một màn hình đoán học vị của
              bác sĩ là một màn hình sẽ đoán sai. */}
          <p className="text-sm text-muted-foreground">
            <PatientLink id={rx.patientId} name={rx.patientName} />
            {rx.patientCode && ` · ${rx.patientCode}`} — {rx.doctorName} —{" "}
            {formatDate(rx.createdAt)}
          </p>
          {/* Chẩn đoán chụp lại lúc kê — trước đây phải mở lại lượt khám mới biết đơn
              này kê cho bệnh gì. */}
          {rx.icd10Code && (
            <p className="text-sm text-muted-foreground">
              Chẩn đoán:{" "}
              <strong className="font-mono text-foreground">{rx.icd10Code}</strong>
              {rx.icd10LabelVi ? ` — ${rx.icd10LabelVi}` : ""}
              {rx.icd10AllCodes && rx.icd10AllCodes !== rx.icd10Code && (
                <span className="text-xs"> · Kèm theo: {rx.icd10AllCodes}</span>
              )}
            </p>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {rx.status === "DRAFT" && (
            <>
              <Button
                type="button"
                disabled={finalize.isPending || answering || allergyGate !== null}
                onClick={() => void startFinalize()}
              >
                Hoàn tất đơn (ký giấy)
              </Button>
              <Button
                type="button"
                variant="secondary"
                disabled={cancel.isPending}
                onClick={() => {
                  const reason = window.prompt("Lý do hủy đơn nháp?");
                  if (reason != null) {
                    setError(null);
                    cancel.mutate(reason || "Hủy");
                  }
                }}
              >
                Hủy
              </Button>
            </>
          )}
          {rx.status === "SIGNED" && (
            <>
              <Button asChild>
                <Link to={`/prescriptions/${rx.id}/print`}>
                  <Printer />
                  In đơn (A5)
                </Link>
              </Button>
              <Button
                type="button"
                variant="secondary"
                disabled={cancel.isPending}
                onClick={() => {
                  const reason = window.prompt("Lý do hủy đơn?");
                  if (reason != null) {
                    setError(null);
                    cancel.mutate(reason || "Hủy");
                  }
                }}
              >
                Hủy đơn
              </Button>
            </>
          )}
        </div>
      </div>

      {error && <p className="mb-4 text-sm text-destructive">{error}</p>}

      <div className="rounded-md border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-8">#</TableHead>
              <TableHead>Thuốc</TableHead>
              <TableHead>Liều dùng</TableHead>
              <TableHead>Tần suất</TableHead>
              <TableHead>Đường dùng</TableHead>
              <TableHead className="text-right">Số lượng</TableHead>
              <TableHead>Hướng dẫn</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rx.items.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={7}
                  className="py-8 text-center text-muted-foreground"
                >
                  Đơn chưa có thuốc.
                </TableCell>
              </TableRow>
            )}
            {rx.items.map((it, i) => (
              <TableRow key={it.id}>
                <TableCell>{i + 1}</TableCell>
                <TableCell>
                  {it.medicineName}
                  {it.medicineStrength && (
                    <span className="text-muted-foreground">
                      {" "}
                      · {it.medicineStrength}
                    </span>
                  )}
                </TableCell>
                <TableCell>{it.dosage}</TableCell>
                <TableCell>{it.frequency}</TableCell>
                <TableCell>{it.route}</TableCell>
                <TableCell className="text-right">
                  {it.quantity} {it.unit}
                </TableCell>
                <TableCell>{it.instructions ?? "—"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* #19184 — the same dialog the encounter screen uses before signing the visit. */}
      <DrugAllergyWarningDialog
        open={allergyGate !== null && allergyGate.length > 0}
        medicineName={allergyGate?.[0]?.medicineName ?? ""}
        warnings={allergyGate?.[0]?.warnings ?? []}
        onCancel={() => {
          setAllergyGate(null);
          setError(ALLERGY_DECLINED_MESSAGE);
        }}
        onConfirm={(reasonCode, reasonText) => void answerAllergyGate(reasonCode, reasonText)}
      />

      {rx.status === "SIGNED" && <DispensePanel prescriptionId={rx.id} />}
      {rx.status === "SIGNED" && <GovRxPanel prescriptionId={rx.id} />}
    </div>
  );
}
